import React, { useState, useEffect, useRef } from 'react';
import { 
  FiAward, 
  FiRefreshCw, 
  FiCheckCircle, 
  FiXCircle,
  FiLoader,
  FiSave,
  FiFileText,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiDownload,
  FiPrinter,
  FiShare2,
  FiGrid,
  FiList,
  FiSearch,
  FiFilter,
  FiClock,
  FiCalendar,
  FiUser,
  FiBookOpen,
  FiStar,
  FiTrendingUp,
  FiSettings
} from "react-icons/fi";
import { 
  MdOutlineCertification, 
  MdOutlineVerified,
  MdOutlineDownload,
  MdOutlineVisibility,
  MdOutlineEdit,
  MdOutlineDelete,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePending,
  MdOutlineSave,
  MdOutlineFileCopy,
  MdOutlinePrint,
  MdOutlineShare
} from "react-icons/md";
import { IoDocumentTextOutline, IoTrophyOutline, IoRibbonOutline } from "react-icons/io5";
import DOMPurify from 'dompurify';
import { apiRequest } from '../../../_helpers/apiConfig';
import { toast } from 'react-toastify';

const CertificateTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const stylesRef = useRef({});

  // Sample data to populate placeholders in the preview
  const sampleData = {
    userName: 'John Doe',
    courseName: 'Web Development Course',
    completionDate: '11 April 2025',
    signerTitle: 'Course Instructor',
    signerName: 'Jane Smith',
    teamName: 'CodeAcademy'
  };

  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await apiRequest('POST', 'v1/appSetting/getCertificate');

        // Process templates to extract styles
        const templatesWithStyles = response?.data?.list.map((template, index) => {
          if (template?.active_certificate) {
            setSelectedId(template._id);
          }
          // Extract styles from HTML
          const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
          const styleMatches = [...template.value.matchAll(styleRegex)];

          // Store extracted styles in ref
          if (styleMatches.length > 0) {
            stylesRef.current[index] = styleMatches.map(match => match[1]).join('\n');
          }

          return template;
        });

        setTemplates(templatesWithStyles || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError('Failed to load certificate templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Process HTML template to replace placeholders with sample data
  const processTemplate = (htmlTemplate) => {
    if (!htmlTemplate) return '';

    // Remove style tags as we'll add them separately
    let processedHtml = htmlTemplate.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    Object.keys(sampleData).forEach(key => {
      const placeholder = new RegExp(`{{${key}}}`, 'g');
      processedHtml = processedHtml.replace(placeholder, sampleData[key]);
    });

    return processedHtml;
  };

  const updateCertificate = async () => {
    if (selectedId) {
      try {
        setIsSaving(true);
        const response = await apiRequest('POST', 'v1/appSetting/certificateSet', { id: selectedId });
        toast.success(response?.message || "Certificate template updated successfully!");
      } catch (err) {
        toast.error(err?.message || "Failed to update certificate template");
      } finally {
        setIsSaving(false);
      }
    }
  }

  // Create safe HTML markup
  const createMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };

  // Count templates
  const totalTemplates = templates.length;
  const activeTemplate = templates.find(t => t._id === selectedId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <FiLoader className="w-10 h-10 text-[#715EFE] animate-spin" />
          <p className="text-sm font-medium text-gray-500">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="p-4 bg-rose-50 rounded-full">
            <FiXCircle className="w-10 h-10 text-rose-500" />
          </div>
          <p className="text-sm font-medium text-gray-700">Error: {error}</p>
          <p className="text-xs text-gray-400">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/80">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <IoRibbonOutline className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">Certificate Templates</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {totalTemplates} templates • {activeTemplate ? '1 active' : 'No active template'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full border border-gray-200">
              <FiClock className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500">
                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {/* Templates Grid */}
          {templates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {templates.map((template, i) => {
                const isActive = selectedId === template?._id;
                return (
                  <div 
                    key={i} 
                    onClick={() => setSelectedId(template._id)}
                    className={`group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border-2 overflow-hidden ${
                      isActive 
                        ? 'border-[#715EFE] shadow-lg shadow-[#715EFE]/10' 
                        : 'border-gray-200 hover:border-[#715EFE]/50'
                    }`}
                  >
                    {/* Status Badge */}
                    {isActive && (
                      <div className="absolute top-3 right-3 z-10">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-white bg-[#715EFE] rounded-full shadow-lg shadow-[#715EFE]/30">
                          <FiCheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="p-4 pb-2">
                      <div className="relative bg-gray-50 rounded-lg overflow-hidden" style={{ height: '220px' }}>
                        {/* Inject extracted styles */}
                        {stylesRef.current[i] && (
                          <style dangerouslySetInnerHTML={{ __html: stylesRef.current[i] }} />
                        )}
                        <div 
                          className="template-preview-content"
                          dangerouslySetInnerHTML={createMarkup(processTemplate(template.value))}
                          style={{
                            transform: 'scale(0.25)',
                            transformOrigin: 'top left',
                            width: '400%',
                            height: '400%',
                            overflow: 'hidden',
                          }}
                        />
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                          Certificate #{i + 1}
                        </p>
                        <p className="text-xs text-gray-400">
                          {template?.active_certificate ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {!isActive && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedId(template._id);
                            }}
                            className="p-1.5 text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                            title="Set as Active"
                          >
                            <FiCheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Preview full size
                          }}
                          className="p-1.5 text-gray-400 hover:text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                          title="Preview Full Size"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-gray-50 rounded-full mb-3">
                <IoRibbonOutline className="w-12 h-12 text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">No templates found</p>
              <p className="text-xs text-gray-400">Certificate templates will appear here</p>
            </div>
          )}

          {/* Save Button */}
          {templates.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <FiSettings className="w-4 h-4 text-[#715EFE]" />
                <span>
                  {activeTemplate 
                    ? `Active template: Certificate #${templates.findIndex(t => t._id === selectedId) + 1}` 
                    : 'No template selected'}
                </span>
              </div>
              <button
                className="flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#855DF6] to-[#715EFE] rounded-lg hover:shadow-lg hover:shadow-[#715EFE]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                onClick={updateCertificate}
                disabled={!selectedId || isSaving}
              >
                {isSaving ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    <span>Save Template</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .template-preview-content {
          transform: scale(0.25);
          transform-origin: top left;
          width: 400%;
          height: 400%;
          overflow: hidden;
        }
        
        @media (max-width: 768px) {
          .template-preview-content {
            transform: scale(0.3);
            width: 333%;
            height: 333%;
          }
        }
        
        @media (max-width: 480px) {
          .template-preview-content {
            transform: scale(0.35);
            width: 285%;
            height: 285%;
          }
        }
      `}</style>
    </div>
  );
};

export default CertificateTemplate;